import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, api } from './client';

/**
 * Fetch a resource, with loading, error, and manual reload.
 *
 * Aborts in flight when the path changes or the component unmounts, so a slow
 * response for a client record the coordinator has already navigated away from
 * cannot overwrite the one they are now looking at.
 */
export function useApi<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (!path) {
      setLoading(false);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    try {
      const result = await api.get<T>(path, controller.signal);
      if (!controller.signal.aborted) {
        setData(result);
        setError(null);
      }
    } catch (caught) {
      if ((caught as Error).name === 'AbortError') return;
      setError(caught as ApiError);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void load();
    return () => controllerRef.current?.abort();
  }, [load]);

  return { data, error, loading, reload: load, setData };
}
